import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Checkbox from "src/components/Checkbox";
import { Spinner } from "react-bootstrap";
import TimeAndAttendanceQRcode from "../../TimeAndAttendance/QRCode";
import { DBLocation, ID } from "src/types";
import { FirebaseDataService } from "src/services/Firebase/data";
import { colors } from "src/styles";
import { filterActiveLocations } from "src/utils/location";

type AttendanceQRProps = {
  clientId: ID;
  locationId: ID;
  location: DBLocation;
  printLocations?: Pick<DBLocation, "id" | "label">[];
};

const MAX_PRINT_LOCATIONS = 4;

export const AttendanceQR = ({ clientId, locationId, location, printLocations }: AttendanceQRProps) => {
  const [isLocationsLoading, setIsLocationsLoading] = useState<boolean>(false);
  const [availableLocations, setAvailableLocations] = useState<Pick<DBLocation, "id" | "label">[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<ID[]>([]);
  const [openQuadrantModalTrigger, setOpenQuadrantModalTrigger] = useState<number>(0);
  const [activeQuadrantLocation, setActiveQuadrantLocation] = useState<Pick<DBLocation, "id" | "label"> | null>(null);
  const [locationQuadrantAssignments, setLocationQuadrantAssignments] = useState<Record<string, number>>({});

  useEffect(() => {
    let isMounted = true;

    const loadClientLocations = async () => {
      if (!clientId) {
        return;
      }

      setIsLocationsLoading(true);

      try {
        const { data } = await FirebaseDataService.getLocationsByClientId(clientId);
        const fetchedLocations = filterActiveLocations(data || [])
          .map((item) => ({ id: item.id, label: item.label }))
          .filter((item) => Boolean(item.id) && Boolean(item.label));

        const currentLocation = { id: locationId, label: location.label };
        const mergedLocations = [...fetchedLocations, currentLocation].reduce<Pick<DBLocation, "id" | "label">[]>(
          (result, item) => {
            if (!result.find((locationItem) => locationItem.id?.toString() === item.id?.toString())) {
              result.push({ id: item.id ? String(item.id) : undefined, label: item.label });
            }

            return result;
          },
          [],
        );

        if (isMounted) {
          setAvailableLocations(mergedLocations);
          setSelectedLocationIds((prev) => {
            if (prev.length > 0) {
              return prev.filter((selectedId) =>
                mergedLocations.some((locationItem) => locationItem.id?.toString() === selectedId.toString()),
              );
            }

            return currentLocation.id ? [currentLocation.id] : [];
          });
        }
      } catch (error) {
        console.error("Failed to load locations for QR printing:", error);

        if (isMounted) {
          const fallbackLocation: Pick<DBLocation, "id" | "label"> = {
            id: locationId as string | undefined,
            label: location.label,
          };
          setAvailableLocations([fallbackLocation]);
          setSelectedLocationIds(fallbackLocation.id ? [fallbackLocation.id] : []);
        }
      } finally {
        if (isMounted) {
          setIsLocationsLoading(false);
        }
      }
    };

    loadClientLocations();

    return () => {
      isMounted = false;
    };
  }, [clientId, locationId, location.label]);

  const selectedPrintLocations = useMemo(() => {
    if (printLocations && printLocations.length > 0) {
      return printLocations.slice(0, MAX_PRINT_LOCATIONS);
    }

    return availableLocations
      .filter((locationItem) =>
        selectedLocationIds.some((selectedId) => selectedId.toString() === locationItem.id?.toString()),
      )
      .slice(0, MAX_PRINT_LOCATIONS);
  }, [availableLocations, printLocations, selectedLocationIds]);

  const onLocationToggle = (toggledLocationId: ID, checked: boolean) => {
    const toggledLocation = availableLocations.find(
      (locationItem) => locationItem.id?.toString() === toggledLocationId.toString(),
    );

    let shouldOpenQuadrantModal = false;

    setSelectedLocationIds((prev) => {
      if (!checked) {
        setLocationQuadrantAssignments((previousAssignments) => {
          const updatedAssignments = { ...previousAssignments };
          delete updatedAssignments[toggledLocationId.toString()];
          return updatedAssignments;
        });

        return prev.filter((id) => id.toString() !== toggledLocationId.toString());
      }

      if (prev.length >= MAX_PRINT_LOCATIONS) {
        return prev;
      }

      if (prev.some((id) => id.toString() === toggledLocationId.toString())) {
        return prev;
      }

      shouldOpenQuadrantModal = true;
      return [...prev, toggledLocationId];
    });

    if (checked && shouldOpenQuadrantModal && toggledLocation) {
      setActiveQuadrantLocation(toggledLocation);
      setOpenQuadrantModalTrigger((prev) => prev + 1);
    }
  };

  const handleQuadrantAssign = (locationIdToAssign: ID, quadrant: number) => {
    setLocationQuadrantAssignments((prev) => ({
      ...prev,
      [locationIdToAssign.toString()]: quadrant,
    }));
  };

  const handleRequestQuadrantSelection = (locationForSelection: Pick<DBLocation, "id" | "label">) => {
    setActiveQuadrantLocation(locationForSelection);
    setOpenQuadrantModalTrigger((prev) => prev + 1);
  };

  const handleClearAllSelections = () => {
    setSelectedLocationIds([]);
    setLocationQuadrantAssignments({});
  };

  return (
    <Wrapper>
      {!printLocations && (
        <SelectorCard>
          <SelectorHeader>
            <SelectorTitle>Select up to 4 locations for one A4 print</SelectorTitle>
            <SelectorCounter>
              {selectedPrintLocations.length}/{MAX_PRINT_LOCATIONS} selected
            </SelectorCounter>
          </SelectorHeader>

          {isLocationsLoading ? (
            <SelectorLoading>
              <Spinner animation="border" size="sm" />
            </SelectorLoading>
          ) : (
            <LocationsList>
              {availableLocations.map((locationItem) => {
                const itemId = locationItem.id;

                if (!itemId) {
                  return null;
                }

                const isChecked = selectedLocationIds.some((selectedId) => selectedId.toString() === itemId.toString());
                const isDisabled = !isChecked && selectedLocationIds.length >= MAX_PRINT_LOCATIONS;

                return (
                  <LocationRow key={itemId.toString()}>
                    <Checkbox
                      checked={isChecked}
                      label={locationItem.label || ""}
                      onChange={(event) => onLocationToggle(itemId, event.target.checked)}
                      containerStyle="justify-content: flex-start;"
                    />
                    {isDisabled ? <DisabledHint>Max reached</DisabledHint> : null}
                  </LocationRow>
                );
              })}
            </LocationsList>
          )}
        </SelectorCard>
      )}

      <TimeAndAttendanceQRcode
        clientId={clientId}
        locationId={locationId}
        location={location}
        printLocations={selectedPrintLocations}
        openQuadrantModalTrigger={openQuadrantModalTrigger}
        activeQuadrantLocation={activeQuadrantLocation}
        locationQuadrantAssignments={locationQuadrantAssignments}
        onAssignQuadrant={handleQuadrantAssign}
        onRequestQuadrantSelection={handleRequestQuadrantSelection}
        onClearAllSelections={handleClearAllSelections}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SelectorCard = styled.div`
  border: 1px solid ${colors.grey.light1};
  border-radius: 8px;
  padding: 16px;
  background: ${colors.white.default};
`;

const SelectorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SelectorTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  color: ${colors.black.dark1};
`;

const SelectorCounter = styled.span`
  font-size: 12px;
  color: ${colors.grey.dark2};
`;

const SelectorLoading = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 0;
`;

const LocationsList = styled.div`
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LocationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const DisabledHint = styled.span`
  font-size: 12px;
  color: ${colors.grey.dark2};
`;

export default AttendanceQR;
